// EduBridge Network — Client-admin panel that edits company address, phones,
// emails and social links used across the public site (header, footer, contact
// page). Backed by /api/site-settings and /api/public/site-settings.
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Save, Plus, Trash2, Loader2, Globe, MapPin, Phone, Mail, Upload, Image as ImageIcon, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { toast } from 'sonner';
import { refreshSiteSettings } from '../../hooks/useSiteSettings';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
  company_name: '',
  primary_address: '',
  secondary_address: '',
  phones: [''],
  emails: [''],
  logo_url: '',
  social: { facebook: '', instagram: '', linkedin: '', youtube: '', twitter: '', tiktok: '' },
};

const authHeader = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function PublicSiteSettingsPanel() {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}/site-settings`, { headers: authHeader() })
      .then((res) => {
        const d = res.data || {};
        setForm({
          company_name: d.company_name || '',
          primary_address: d.primary_address || '',
          secondary_address: d.secondary_address || '',
          phones: (d.phones && d.phones.length ? d.phones : ['']),
          emails: (d.emails && d.emails.length ? d.emails : ['']),
          logo_url: d.logo_url || '',
          social: { ...emptyForm.social, ...(d.social || {}) },
        });
      })
      .catch(() => toast.error('Could not load site settings'))
      .finally(() => setLoading(false));
  }, []);

  const updateSocial = (key, value) => setForm((f) => ({ ...f, social: { ...f.social, [key]: value } }));
  const updateList = (key, idx, value) =>
    setForm((f) => ({ ...f, [key]: f[key].map((v, i) => (i === idx ? value : v)) }));
  const addToList = (key) => setForm((f) => ({ ...f, [key]: [...f[key], ''] }));
  const removeFromList = (key, idx) =>
    setForm((f) => ({ ...f, [key]: f[key].filter((_, i) => i !== idx) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        phones: form.phones.map((p) => p.trim()).filter(Boolean),
        emails: form.emails.map((e) => e.trim()).filter(Boolean),
      };
      // logo_url is managed separately via upload/delete — don't send it in the JSON PUT
      delete payload.logo_url;
      await axios.put(`${API}/site-settings`, payload, { headers: authHeader() });
      await refreshSiteSettings();
      toast.success('Site settings saved — public site updated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const onPickLogo = () => fileInputRef.current?.click();

  const onLogoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo must be under 5MB');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/site-settings/logo`, fd, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
      });
      setForm((f) => ({ ...f, logo_url: res.data.logo_url }));
      await refreshSiteSettings();
      toast.success('Logo uploaded — public site updated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const removeLogo = async () => {
    if (!window.confirm('Remove the uploaded logo? Public site will fall back to your brand name text.')) return;
    setUploading(true);
    try {
      await axios.delete(`${API}/site-settings/logo`, { headers: authHeader() });
      setForm((f) => ({ ...f, logo_url: '' }));
      await refreshSiteSettings();
      toast.success('Logo removed');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Remove failed');
    } finally {
      setUploading(false);
    }
  };

  const logoSrc = form.logo_url
    ? (form.logo_url.startsWith('http') ? form.logo_url : `${BASE}${form.logo_url}`)
    : '';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="site-settings-loading">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="public-site-settings-panel">
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Globe className="w-6 h-6 text-primary" /> Company Info (Public Site)
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          These details appear on the public header, footer, contact page and marketing pages that visitors browse before signing up.
        </p>
      </div>

      {/* Basics */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Brand</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Company name</Label>
            <Input
              value={form.company_name}
              onChange={(e) => setForm({ ...form, company_name: e.target.value })}
              placeholder="Edu Bridge Network"
              data-testid="site-company-name"
            />
          </div>
        </CardContent>
      </Card>

      {/* Logo */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><ImageIcon className="w-4 h-4" /> Logo</CardTitle>
          <CardDescription>
            Shown in the public header instead of the plain-text brand name. Recommended: transparent PNG or SVG, at least 240 × 60 px, max 5 MB.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <div className="w-56 h-20 rounded-md border border-dashed border-gray-300 bg-white flex items-center justify-center overflow-hidden" data-testid="site-logo-preview">
              {logoSrc ? (
                <img src={logoSrc} alt="Current logo" className="max-h-full max-w-full object-contain" />
              ) : (
                <span className="text-xs text-muted-foreground px-3 text-center">No logo yet — brand name text will be shown</span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={onLogoChange}
                className="hidden"
                data-testid="site-logo-input"
              />
              <Button onClick={onPickLogo} disabled={uploading} data-testid="site-logo-upload-btn">
                {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4 mr-2" /> {logoSrc ? 'Replace logo' : 'Upload logo'}</>}
              </Button>
              {logoSrc && (
                <Button variant="outline" onClick={removeLogo} disabled={uploading} data-testid="site-logo-remove-btn">
                  <X className="w-4 h-4 mr-2" /> Remove
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Address */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><MapPin className="w-4 h-4" /> Addresses</CardTitle>
          <CardDescription>Primary shows on the orange contact bar; Secondary shows in the footer &amp; contact page.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Primary address (shown in top header bar)</Label>
            <Input
              value={form.primary_address}
              onChange={(e) => setForm({ ...form, primary_address: e.target.value })}
              placeholder="180 West Road Suite 300 Towson, MD 21204"
              data-testid="site-primary-address"
            />
          </div>
          <div>
            <Label>Secondary address (footer &amp; contact page — supports multiple lines)</Label>
            <Textarea
              rows={3}
              value={form.secondary_address}
              onChange={(e) => setForm({ ...form, secondary_address: e.target.value })}
              placeholder={"Maple Visa Services Inc.\n525 Bay Street, Suite 700\nToronto, ON M5G 2L2, Canada"}
              data-testid="site-secondary-address"
            />
          </div>
        </CardContent>
      </Card>

      {/* Phones */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Phone className="w-4 h-4" /> Phone numbers</CardTitle>
          <CardDescription>First number appears in the header. All numbers appear on the contact page.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {form.phones.map((p, i) => (
            <div key={i} className="flex gap-2 items-center">
              <Input
                value={p}
                onChange={(e) => updateList('phones', i, e.target.value)}
                placeholder="+44 7985 262065"
                data-testid={`site-phone-${i}`}
              />
              <Button variant="ghost" size="icon" onClick={() => removeFromList('phones', i)} disabled={form.phones.length <= 1} data-testid={`site-phone-remove-${i}`}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => addToList('phones')} data-testid="site-phone-add">
            <Plus className="w-4 h-4 mr-1" /> Add phone
          </Button>
        </CardContent>
      </Card>

      {/* Emails */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Mail className="w-4 h-4" /> Email addresses</CardTitle>
          <CardDescription>First email appears in the header. All emails appear on the contact page and footer.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {form.emails.map((em, i) => (
            <div key={i} className="flex gap-2 items-center">
              <Input
                type="email"
                value={em}
                onChange={(e) => updateList('emails', i, e.target.value)}
                placeholder="study_abroad@gmail.com"
                data-testid={`site-email-${i}`}
              />
              <Button variant="ghost" size="icon" onClick={() => removeFromList('emails', i)} disabled={form.emails.length <= 1} data-testid={`site-email-remove-${i}`}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => addToList('emails')} data-testid="site-email-add">
            <Plus className="w-4 h-4 mr-1" /> Add email
          </Button>
        </CardContent>
      </Card>

      {/* Social */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Social media links</CardTitle>
          <CardDescription>Leave blank to hide the icon on the public site.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { key: 'facebook', label: 'Facebook URL', placeholder: 'https://facebook.com/your-page' },
            { key: 'instagram', label: 'Instagram URL', placeholder: 'https://instagram.com/your-handle' },
            { key: 'linkedin', label: 'LinkedIn URL', placeholder: 'https://linkedin.com/company/your-org' },
            { key: 'youtube', label: 'YouTube URL', placeholder: 'https://youtube.com/@your-channel' },
            { key: 'twitter', label: 'Twitter / X URL', placeholder: 'https://x.com/your-handle' },
            { key: 'tiktok', label: 'TikTok URL', placeholder: 'https://tiktok.com/@your-handle' },
          ].map((f) => (
            <div key={f.key}>
              <Label>{f.label}</Label>
              <Input
                value={form.social[f.key] || ''}
                onChange={(e) => updateSocial(f.key, e.target.value)}
                placeholder={f.placeholder}
                data-testid={`site-social-${f.key}`}
              />
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="sticky bottom-0 bg-white border-t py-3 flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="site-settings-save-btn">
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> Save changes</>}
        </Button>
      </div>
    </div>
  );
}
